/**
 * event-tracking — data layer (feat #169).
 *
 * Thin typed wrapper over `apiClient` for:
 *   POST   /api/v1/event-tracking/definitions
 *   GET    /api/v1/event-tracking/definitions
 *   POST   /api/v1/event-tracking/definitions/:id/archive
 *   POST   /api/v1/event-tracking/definitions/:id/activate
 *   POST   /api/v1/event-tracking
 *   GET    /api/v1/event-tracking
 *   GET    /api/v1/event-tracking/stats
 *
 * Pure helpers (validators, formatters, query keys) are exported so the page
 * component and the unit tests can share contracts with the server.
 */
import { apiClient } from "../../api-client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EventCategory =
	| "ecommerce"
	| "engagement"
	| "auth"
	| "game"
	| "custom";

export type EventDefinitionStatus = "active" | "archived";

export interface EventDefinition {
	id: string;
	event_name: string;
	category: EventCategory;
	description: string | null;
	status: EventDefinitionStatus;
	value_enabled: boolean;
	created_at: number;
	updated_at: number;
}

export interface CustomEvent {
	id: string;
	event_name: string;
	click_id: string | null;
	affiliate_id: string | null;
	value_cents: number | null;
	currency: string | null;
	payload: Record<string, unknown>;
	occurred_at: number;
	received_at: number;
}

export interface ListDefinitionsResponse {
	count: number;
	items: EventDefinition[];
}

export interface ListEventsResponse {
	count: number;
	items: CustomEvent[];
}

export interface EventStatRow {
	event_name: string;
	count: number;
	total_value_cents: number;
}

export interface StatsResponse {
	stats: EventStatRow[];
}

// ---------------------------------------------------------------------------
// Validators - mirror the server contract so the UI can short-circuit bad
// input before the network round-trip.
// ---------------------------------------------------------------------------

const EVENT_NAME_RE = /^[a-z][a-z0-9_]{0,63}$/;
const CURRENCY_RE = /^[a-z]{3}$/;
const ID_RE = /^[a-zA-Z0-9_\-]{1,128}$/;

export const DESCRIPTION_MAX = 500;
export const CATEGORIES: readonly EventCategory[] = [
	"ecommerce",
	"engagement",
	"auth",
	"game",
	"custom",
];

export function normalizeEventName(raw: string): string {
	return String(raw ?? "").trim().toLowerCase();
}

export function validateEventName(raw: string): string | null {
	const normalized = normalizeEventName(raw);
	if (!EVENT_NAME_RE.test(normalized)) return null;
	return normalized;
}

export function validateCategory(raw: string): EventCategory | null {
	return (CATEGORIES as readonly string[]).includes(raw)
		? (raw as EventCategory)
		: null;
}

export function validateDescription(
	raw: string | null | undefined,
): string | null | "invalid" {
	if (raw == null) return null;
	const trimmed = raw.trim();
	if (!trimmed) return null;
	if (trimmed.length > DESCRIPTION_MAX) return "invalid";
	return trimmed;
}

export function validateCurrency(
	raw: string | null | undefined,
): string | null | "invalid" {
	if (raw == null) return null;
	const trimmed = raw.trim().toLowerCase();
	if (!trimmed) return null;
	if (!CURRENCY_RE.test(trimmed)) return "invalid";
	return trimmed;
}

export function validateValueCents(
	raw: string | null | undefined,
): number | null | "invalid" {
	if (raw == null || raw === "") return null;
	const trimmed = String(raw).trim();
	if (!trimmed) return null;
	const n = Number(trimmed);
	if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0 || n > 100_000_000_000) {
		return "invalid";
	}
	return n;
}

export function validateIdLike(
	raw: string | null | undefined,
): string | null | "invalid" {
	if (raw == null) return null;
	const trimmed = String(raw).trim();
	if (!trimmed) return null;
	if (!ID_RE.test(trimmed)) return "invalid";
	return trimmed;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const eventTrackingQueryKeys = {
	all: ["event-tracking"] as const,
	definitions: () => [...eventTrackingQueryKeys.all, "definitions"] as const,
	events: () => [...eventTrackingQueryKeys.all, "events"] as const,
	stats: () => [...eventTrackingQueryKeys.all, "stats"] as const,
};

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export function formatDate(unixSec: number): string {
	if (!Number.isFinite(unixSec) || unixSec <= 0) return "—";
	return new Date(unixSec * 1000).toLocaleString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export function formatCents(
	cents: number | null,
	currency: string | null,
): string {
	if (cents == null) return "—";
	const dollars = cents / 100;
	const code = (currency ?? "USD").toUpperCase();
	try {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: code,
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(dollars);
	} catch {
		return `${dollars.toFixed(2)} ${code}`;
	}
}

export function formatEventCount(n: number): string {
	if (!Number.isFinite(n) || n < 0) return "0 events";
	return n === 1 ? "1 event" : `${n.toLocaleString("en-US")} events`;
}

export function categoryLabel(cat: EventCategory): string {
	switch (cat) {
		case "ecommerce":
			return "E-commerce";
		case "engagement":
			return "Engagement";
		case "auth":
			return "Auth";
		case "game":
			return "Game";
		case "custom":
			return "Custom";
	}
}

// ---------------------------------------------------------------------------
// Remote bindings
// ---------------------------------------------------------------------------

const BASE = "/event-tracking";

export interface CreateDefinitionArgs {
	event_name: string;
	category?: EventCategory;
	description?: string | null;
	value_enabled?: boolean;
}

export async function createDefinition(
	args: CreateDefinitionArgs,
): Promise<EventDefinition> {
	const payload: Record<string, unknown> = {
		eventName: args.event_name,
	};
	if (args.category) payload.category = args.category;
	if (args.description) payload.description = args.description;
	if (args.value_enabled !== undefined) payload.valueEnabled = args.value_enabled;
	const res = await apiClient.post<{ definition: EventDefinition }>(
		`${BASE}/definitions`,
		payload,
	);
	return res.definition;
}

export async function listDefinitions(): Promise<ListDefinitionsResponse> {
	return apiClient.get<ListDefinitionsResponse>(`${BASE}/definitions`);
}

export async function archiveDefinition(id: string): Promise<EventDefinition> {
	const res = await apiClient.post<{ definition: EventDefinition }>(
		`${BASE}/definitions/${encodeURIComponent(id)}/archive`,
		{},
	);
	return res.definition;
}

export async function activateDefinition(id: string): Promise<EventDefinition> {
	const res = await apiClient.post<{ definition: EventDefinition }>(
		`${BASE}/definitions/${encodeURIComponent(id)}/activate`,
		{},
	);
	return res.definition;
}

export interface CaptureArgs {
	event_name: string;
	click_id?: string | null;
	affiliate_id?: string | null;
	value_cents?: number | null;
	currency?: string | null;
	payload?: Record<string, unknown>;
}

export async function captureEvent(args: CaptureArgs): Promise<CustomEvent> {
	const payload: Record<string, unknown> = {
		eventName: args.event_name,
	};
	if (args.click_id) payload.clickId = args.click_id;
	if (args.affiliate_id) payload.affiliateId = args.affiliate_id;
	if (args.value_cents != null) payload.valueCents = args.value_cents;
	if (args.currency) payload.currency = args.currency;
	if (args.payload) payload.payload = args.payload;
	const res = await apiClient.post<{ event: CustomEvent }>(BASE, payload);
	return res.event;
}

export async function listEvents(limit = 50): Promise<ListEventsResponse> {
	return apiClient.get<ListEventsResponse>(BASE, {
		query: { limit },
	});
}

export async function fetchStats(): Promise<StatsResponse> {
	return apiClient.get<StatsResponse>(`${BASE}/stats`);
}
